// SPDX-License-Identifier: AGPL-3.0-or-later
import { useMemo, useState } from "react";
import { useApp } from "../lib/app.js";
import { needsCategory } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";
import { CategoryOptions } from "../components/CategoryOptions.js";
import { useStore } from "../lib/store.js";

/** Rows drawn at once; "Show more" adds this many again. */
const PAGE = 200;
const ALL = "";

export function Transactions() {
  const { store, announce } = useApp();
  const state = useStore(store);
  const { t, money, date, categoryPath } = useI18n();

  const months = useMemo(() => [...new Set(state.ledger.map((tx) => tx.date.slice(0, 7)))], [state.ledger]);
  const waiting = useMemo(() => state.ledger.filter(needsCategory).length, [state.ledger]);
  const accountName = useMemo(() => new Map(state.accounts.map((a) => [a.id, a.name])), [state.accounts]);

  // #/transactions?review opens the queue of uncategorised transactions.
  const [review, setReview] = useState(() => location.hash.includes("?review"));
  const [month, setMonth] = useState(() => (review ? ALL : (months[0] ?? ALL)));
  const [account, setAccount] = useState(ALL);
  const [search, setSearch] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulk, setBulk] = useState("");

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return state.ledger.filter(
      (tx) =>
        (month === ALL || tx.date.startsWith(month)) &&
        (account === ALL || tx.accountId === account) &&
        (!review || needsCategory(tx)) &&
        (!needle || tx.description.toLowerCase().includes(needle)),
    );
  }, [state.ledger, month, account, review, search]);

  const visible = rows.slice(0, shown);
  const chosen = rows.filter((tx) => selected.has(tx.id));
  const allChosen = visible.length > 0 && visible.every((tx) => selected.has(tx.id));

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const applyBulk = () => {
    if (!bulk || chosen.length === 0) return;
    store.setCategories(
      chosen.map((tx) => tx.id),
      bulk,
    );
    announce(t("tx.bulkDone", { count: chosen.length, category: categoryPath(bulk) }));
    setSelected(new Set());
    setBulk("");
  };

  if (state.ledger.length === 0) {
    return (
      <section className="card">
        <p>{t("tx.empty")}</p>
        <a href="#/import">{t("tx.goImport")}</a>
      </section>
    );
  }

  return (
    <>
      <section className="card" style={{ marginBottom: 16 }} aria-label={t("tx.filters")}>
        <div className="toolbar">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="tx-month">{t("tx.month")}</label>
            <select id="tx-month" value={month} onChange={(e) => (setMonth(e.target.value), setShown(PAGE))}>
              <option value={ALL}>{t("tx.allMonths")}</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {date(`${m}-01`, "month")}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="tx-account">{t("tx.account")}</label>
            <select id="tx-account" value={account} onChange={(e) => (setAccount(e.target.value), setShown(PAGE))}>
              <option value={ALL}>{t("tx.allAccounts")}</option>
              {state.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="tx-search">{t("tx.search")}</label>
            <input id="tx-search" type="search" value={search} onChange={(e) => (setSearch(e.target.value), setShown(PAGE))} />
          </div>
          <label className="row" style={{ fontWeight: 450 }}>
            <input type="checkbox" checked={review} onChange={(e) => (setReview(e.target.checked), setShown(PAGE))} />
            {t("tx.reviewOnly", { count: waiting })}
          </label>
        </div>
      </section>

      <section className="card" aria-labelledby="tx-list-title">
        <h2 id="tx-list-title">{t("tx.count", { count: rows.length })}</h2>
        {rows.length === 0 ? (
          <p>{review ? t("tx.allSorted") : t("tx.noMatch")}</p>
        ) : (
          <>
            <div className="row" style={{ marginBottom: 12 }} role="group" aria-label={t("tx.bulkLabel")}>
              <span>{t("tx.selected", { count: chosen.length })}</span>
              <select aria-label={t("tx.bulkCategory")} value={bulk} onChange={(e) => setBulk(e.target.value)} disabled={chosen.length === 0}>
                <option value="">{t("tx.bulkPick")}</option>
                <CategoryOptions />
              </select>
              <button type="button" className="primary" onClick={applyBulk} disabled={!bulk || chosen.length === 0}>
                {t("tx.bulkApply", { count: chosen.length })}
              </button>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th scope="col">
                      <input
                        type="checkbox"
                        aria-label={t("tx.selectAll")}
                        checked={allChosen}
                        onChange={() => setSelected(allChosen ? new Set() : new Set(visible.map((tx) => tx.id)))}
                      />
                    </th>
                    <th scope="col">{t("import.col.date")}</th>
                    <th scope="col">{t("import.col.description")}</th>
                    <th scope="col">{t("tx.account")}</th>
                    <th scope="col" className="num">
                      {t("import.col.amount")}
                    </th>
                    <th scope="col">{t("import.col.category")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((tx) => (
                    <tr key={tx.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t("tx.select", { description: tx.description })}
                          checked={selected.has(tx.id)}
                          onChange={() => toggle(tx.id)}
                        />
                      </td>
                      <td>{date(tx.date)}</td>
                      <td>{tx.description}</td>
                      <td>{accountName.get(tx.accountId) ?? ""}</td>
                      <td className={`num ${tx.amount < 0 ? "negative" : "positive"}`}>{money(tx.amount)}</td>
                      <td>
                        <select
                          aria-label={`${t("import.col.category")}: ${tx.description}`}
                          value={needsCategory(tx) ? "" : (tx.category ?? "")}
                          onChange={(e) => store.setCategory(tx.id, e.target.value || null)}
                        >
                          <option value="">{t("import.needsCategory")}</option>
                          <CategoryOptions />
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > shown && (
              <button type="button" style={{ marginTop: 12 }} onClick={() => setShown(shown + PAGE)}>
                {t("tx.showMore", { count: Math.min(PAGE, rows.length - shown) })}
              </button>
            )}
            <p className="hint" style={{ marginTop: 12 }}>
              {t("tx.learnHint")}
            </p>
          </>
        )}
      </section>
    </>
  );
}
